import type { DrawerProps } from '@mantine/core'
import { Drawer } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import type { ButtonProps } from 'components/Button/Button'
import type { FC, ReactNode } from 'react'
import { useRef } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { NarrowFooter } from './NarrowFooter/NarrowFooter'
import { NarrowHeader } from './NarrowHeader/NarrowHeader'
import { useBlurFocusWithinWhenClosed } from './useBlurFocusWithinWhenClosed'
import { WideFooter } from './WideFooter/WideFooter'
import { WideHeader } from './WideHeader/WideHeader'

type ModalDrawerProps = {
  size?: DrawerProps['size']
  fullSize?: DrawerProps['size']
  title?: string
  opened: boolean
  onClose: () => void
  isSecondaryModal?: boolean
  primaryButtonAsync?: ButtonProps['async']
  primaryButtonAsyncOptions?: ButtonProps['asyncOptions']
  primaryButtonDisabled?: boolean
  primaryButtonText?: string
  primaryButtonOnPress?: () => void
  secondaryButtonDisabled?: boolean
  secondaryButtonText?: string
  secondaryButtonOnPress?: () => void
  secondaryButtonVariant?: ButtonProps['variant']
  customWideHeader?: ReactNode
  customNarrowHeader?: ReactNode
  customWideFooter?: ReactNode
  customNarrowFooter?: ReactNode
  children?: ReactNode
  zIndex: number
  trapFocus?: boolean
}

export const ModalDrawer: FC<ModalDrawerProps> = ({
  size = '50%',
  fullSize = '100%',
  title = '',
  opened,
  onClose,
  isSecondaryModal,
  primaryButtonAsync,
  primaryButtonAsyncOptions,
  primaryButtonDisabled,
  primaryButtonText,
  primaryButtonOnPress,
  secondaryButtonDisabled,
  secondaryButtonText,
  secondaryButtonOnPress,
  secondaryButtonVariant = 'tertiary',
  customWideHeader,
  customNarrowHeader,
  customWideFooter,
  customNarrowFooter,
  children,
  zIndex,
  trapFocus,
  ...rest
}) => {
  const isWideScreen = useIsWideScreen()
  const { height } = useViewportSize()
  const containerRef = useRef<HTMLDivElement>(null)

  useBlurFocusWithinWhenClosed(containerRef, opened)

  return (
    <Drawer
      closeOnClickOutside={false}
      closeOnEscape={false}
      onClose={onClose}
      opened={opened}
      overlayProps={{ backgroundOpacity: 0.45 }}
      position="right"
      size={isWideScreen ? size : fullSize}
      transitionProps={{ duration: 300, timingFunction: 'ease' }}
      withCloseButton={false}
      withOverlay={isWideScreen || !isSecondaryModal}
      zIndex={zIndex}
      styles={{ body: { padding: 0 } }}
      trapFocus={trapFocus}
      {...rest}
    >
      <div
        ref={containerRef}
        css={{
          height,
          overflowY: isWideScreen ? 'auto' : 'unset',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          [`@media (max-width: ${SharedSettings.MINIMUM_WIDTH_WIDE_SCREEN}px)`]: {
            position: 'fixed',
            height: '100%',
            width: '100%',
          },
        }}
      >
        {(() => {
          if (!isWideScreen) return null

          return (
            customWideHeader || (
              <WideHeader onClose={onClose} title={title} isSecondaryModal={isSecondaryModal} />
            )
          )
        })()}
        {(() => {
          if (isWideScreen) return null

          return (
            <div>
              {customNarrowHeader || (
                <NarrowHeader
                  isSecondaryModal={isSecondaryModal}
                  onClose={onClose}
                  secondaryButtonDisabled={secondaryButtonDisabled}
                  secondaryButtonText={secondaryButtonText}
                  secondaryButtonOnPress={secondaryButtonOnPress}
                  secondaryButtonVariant={secondaryButtonVariant}
                  title={title}
                />
              )}
            </div>
          )
        })()}
        <div css={{ overflowY: 'auto' }}>{children}</div>
        {(() => {
          if (!isWideScreen) return null

          return (
            <div>
              {customWideFooter || (
                <WideFooter
                  primaryButtonAsync={primaryButtonAsync}
                  primaryButtonAsyncOptions={primaryButtonAsyncOptions}
                  primaryButtonDisabled={primaryButtonDisabled}
                  primaryButtonText={primaryButtonText}
                  primaryButtonOnPress={primaryButtonOnPress}
                  secondaryButtonDisabled={secondaryButtonDisabled}
                  secondaryButtonText={secondaryButtonText}
                  secondaryButtonOnPress={secondaryButtonOnPress}
                  secondaryButtonVariant={secondaryButtonVariant}
                />
              )}
            </div>
          )
        })()}
        {(() => {
          if (isWideScreen) return null

          return (
            <div>
              {customNarrowFooter || (
                <NarrowFooter
                  primaryButtonAsync={primaryButtonAsync}
                  primaryButtonAsyncOptions={primaryButtonAsyncOptions}
                  primaryButtonDisabled={primaryButtonDisabled}
                  primaryButtonText={primaryButtonText}
                  primaryButtonOnPress={primaryButtonOnPress}
                />
              )}
            </div>
          )
        })()}
      </div>
    </Drawer>
  )
}
